import React from 'react';
import { useNavigate } from 'react-router-dom';

import AddIcon from '@mui/icons-material/Add';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import PersonIcon from '@mui/icons-material/Person';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import CustomButton from 'components/button/CustomButton';
import Circular from 'components/common/CustomCircular';
import TextArea from 'components/form/TextArea';
import { LanguageChip } from 'components/training';
import { MSG_REQUIRED } from 'constant/ValidationMessages';
import {
  AssignmentDetailResult,
  AssignmentListResult,
  QuestionSourceType,
  addAssignmentQuestions,
  assignAssignment,
  createAssignment,
  getAssignment,
  listAssignment,
  removeAssignmentQuestions,
  setAssignmentHidden,
  updateAssignment,
} from 'stores/TrainingAdminStoreAdapter';
import { errorDefault, successDefault, toErrorDetail } from 'utils/Toastify';

import AddQuestionsDialog from './components/AddQuestionsDialog';
import AssignUsersDialog from './components/AssignUsersDialog';
import {
  ConsoleColumn,
  ConsoleDetailDrawer,
  ConsoleHeader,
  ConsoleTable,
  DrawerSection,
  StatusBadge,
} from './components/console';

const NAME_MAX = 128;
const DESC_MAX = 1024;

/**
 * 課題管理ページ（DEVELOPER）。
 * AWSコンソール風のリソーステーブル（課題名 / 設問数 / 割当 / 状態）＋詳細ドロワー
 * （課題編集・設問一覧・割り当て状況）。
 */
const AssignmentManagement: React.FC = () => {
  const navigate = useNavigate();
  const [isProgress, setProgress] = React.useState(true);
  // 登録・更新・切替などの操作中フラグ（サーキュラー表示・二重送信抑止）
  const [busy, setBusy] = React.useState(false);
  const [assignments, setAssignments] = React.useState<AssignmentListResult[]>([]);
  const [selected, setSelected] = React.useState<AssignmentDetailResult | null>(null);
  const [drawerMode, setDrawerMode] = React.useState<'none' | 'create' | 'edit'>('none');
  const [search, setSearch] = React.useState('');
  const [includeHidden, setIncludeHidden] = React.useState(false);

  // 作成フォーム
  const [newName, setNewName] = React.useState('');
  const [newDesc, setNewDesc] = React.useState('');

  // 編集
  const [editName, setEditName] = React.useState('');
  const [editDesc, setEditDesc] = React.useState('');

  const [addQuestionsOpen, setAddQuestionsOpen] = React.useState(false);
  const [assignUsersOpen, setAssignUsersOpen] = React.useState(false);

  const fetchAssignments = React.useCallback(
    (params: { keyword?: string; includeHidden?: boolean }) => {
      setProgress(true);
      listAssignment(params)
        .then(setAssignments)
        .catch(() => errorDefault('課題一覧の取得に失敗しました'))
        .finally(() => setProgress(false));
    },
    [],
  );

  React.useEffect(() => {
    fetchAssignments({ includeHidden });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeHidden]);

  // 現在適用中の検索条件（キーワード + 非表示含む）で再検索する
  const refetch = () => fetchAssignments({ keyword: search || undefined, includeHidden });

  const doSearch = () => {
    fetchAssignments({ keyword: search || undefined, includeHidden });
  };

  const loadDetail = (assignmentId: string): Promise<void> =>
    getAssignment(assignmentId)
      .then((detail) => {
        setSelected(detail);
        setEditName(detail.name);
        setEditDesc(detail.description);
      })
      .catch(() => errorDefault('課題詳細の取得に失敗しました'));

  const handleRowClick = (a: AssignmentListResult) => {
    setProgress(true);
    loadDetail(a.assignmentId)
      .then(() => setDrawerMode('edit'))
      .finally(() => setProgress(false));
  };

  const closeDrawer = () => {
    setDrawerMode('none');
    setSelected(null);
  };

  const handleCreate = async () => {
    if (busy) return;
    if (newName.trim() === '') {
      errorDefault(MSG_REQUIRED('課題名'));
      return;
    }
    if (newDesc.trim() === '') {
      errorDefault(MSG_REQUIRED('課題説明'));
      return;
    }
    setBusy(true);
    try {
      await createAssignment({ name: newName.trim(), description: newDesc.trim() });
      successDefault('課題を登録しました');
      setNewName('');
      setNewDesc('');
      setDrawerMode('none');
      refetch();
    } catch (e) {
      errorDefault('課題の登録に失敗しました', toErrorDetail(e));
    } finally {
      setBusy(false);
    }
  };

  const handleUpdate = async () => {
    if (!selected || busy) return;
    if (editName.trim() === '') {
      errorDefault(MSG_REQUIRED('課題名'));
      return;
    }
    if (editDesc.trim() === '') {
      errorDefault(MSG_REQUIRED('課題説明'));
      return;
    }
    setBusy(true);
    try {
      await updateAssignment({
        assignmentId: selected.assignmentId,
        name: editName.trim(),
        description: editDesc.trim(),
      });
      successDefault('課題を更新しました');
      const updated = { ...selected, name: editName.trim(), description: editDesc.trim() };
      setSelected(updated);
      setAssignments((prev) =>
        prev.map((a) =>
          a.assignmentId === selected.assignmentId
            ? { ...a, name: updated.name, description: updated.description }
            : a,
        ),
      );
    } catch (e) {
      errorDefault('課題の更新に失敗しました', toErrorDetail(e));
    } finally {
      setBusy(false);
    }
  };

  const handleToggleHidden = async () => {
    if (!selected || busy) return;
    const next = !selected.isHidden;
    setBusy(true);
    try {
      await setAssignmentHidden(selected.assignmentId, next);
      successDefault(next ? '課題を非表示にしました' : '課題を再表示しました');
      setSelected({ ...selected, isHidden: next });
      setAssignments((prev) =>
        prev.map((a) => (a.assignmentId === selected.assignmentId ? { ...a, isHidden: next } : a)),
      );
    } catch (e) {
      errorDefault('非表示切替に失敗しました', toErrorDetail(e));
    } finally {
      setBusy(false);
    }
  };

  const handleAddQuestions = async (questionIds: string[]) => {
    if (!selected || busy) return;
    setBusy(true);
    try {
      await addAssignmentQuestions(selected.assignmentId, questionIds);
      successDefault('設問を追加しました');
      setAddQuestionsOpen(false);
      await loadDetail(selected.assignmentId);
    } catch (e) {
      errorDefault('設問の追加に失敗しました', toErrorDetail(e));
    } finally {
      setBusy(false);
    }
  };

  const handleRemoveQuestion = async (questionId: string) => {
    if (!selected || busy) return;
    setBusy(true);
    try {
      await removeAssignmentQuestions(selected.assignmentId, [questionId]);
      successDefault('設問の追加を取り消しました');
      await loadDetail(selected.assignmentId);
    } catch (e) {
      errorDefault(
        '設問の取消に失敗しました（割り当て済みの課題は取り消せません）',
        toErrorDetail(e),
      );
    } finally {
      setBusy(false);
    }
  };

  const handleAssignUsers = async (userIds: string[]) => {
    if (!selected || busy) return;
    setBusy(true);
    try {
      await assignAssignment(selected.assignmentId, userIds);
      successDefault('課題を割り当てました');
      setAssignUsersOpen(false);
      await loadDetail(selected.assignmentId);
    } catch (e) {
      errorDefault('割り当てに失敗しました', toErrorDetail(e));
    } finally {
      setBusy(false);
    }
  };

  // 未割り当て（割当ユーザーが空）なら設問の追加取消が可能
  const isUnassigned = selected != null && selected.assignedUsers.length === 0;

  const columns: ConsoleColumn<AssignmentListResult>[] = [
    {
      key: 'name',
      header: '課題名',
      render: (a) => (
        <Stack direction="row" alignItems="center" spacing={0.75}>
          {a.isHidden && <VisibilityOffIcon sx={{ fontSize: 15, color: 'text.disabled' }} />}
          <Box component="span" sx={{ fontWeight: 600 }}>
            {a.name}
          </Box>
        </Stack>
      ),
    },
    {
      key: 'description',
      header: '説明',
      render: (a) => (
        <Box
          component="span"
          sx={{
            color: 'text.secondary',
            display: '-webkit-box',
            WebkitLineClamp: 1,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {a.description}
        </Box>
      ),
    },
    {
      key: 'status',
      header: '状態',
      width: 90,
      render: (a) =>
        a.isHidden ? (
          <StatusBadge label="非表示" tone="muted" />
        ) : (
          <StatusBadge label="有効" tone="active" />
        ),
    },
  ];

  return (
    <Box sx={{ height: '100%', overflow: 'hidden' }}>
      <Circular open={isProgress || busy} />
      <ConsoleHeader
        title="課題"
        count={assignments.length}
        description="研修の課題を作成し、設問の追加・ユーザーへの割り当てを行います"
        backLabel="研修管理コンソール"
        onBack={() => navigate('/training-admin')}
        onRefresh={refetch}
        actions={
          <CustomButton
            label="課題を作成"
            buttonVariant="primary"
            icon={<AddIcon />}
            onClick={() => {
              setSelected(null);
              setDrawerMode('create');
            }}
          />
        }
      />

      <ConsoleTable
        columns={columns}
        rows={assignments}
        rowKey={(a) => a.assignmentId}
        selectedKey={drawerMode === 'edit' ? selected?.assignmentId : undefined}
        onRowClick={handleRowClick}
        searchValue={search}
        onSearchChange={setSearch}
        onSearchSubmit={doSearch}
        searchPlaceholder="課題名で検索"
        emptyMessage="課題がありません。「課題を作成」から登録してください。"
        filterActions={
          <Button
            size="small"
            variant={includeHidden ? 'contained' : 'outlined'}
            onClick={() => setIncludeHidden((v) => !v)}
            startIcon={includeHidden ? <VisibilityIcon /> : <VisibilityOffIcon />}
            sx={{
              flexShrink: 0,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: 'none',
              whiteSpace: 'nowrap',
              '&:hover': { boxShadow: 'none' },
            }}
          >
            非表示を含む
          </Button>
        }
      />

      {/* 作成ドロワー */}
      <ConsoleDetailDrawer
        open={drawerMode === 'create'}
        onClose={closeDrawer}
        title="課題の作成"
        width={520}
      >
        <Stack spacing={2}>
          <TextField
            label="課題名"
            required
            size="small"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            slotProps={{ htmlInput: { maxLength: NAME_MAX } }}
            helperText={`${newName.length}/${NAME_MAX}`}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
          <TextArea
            label="課題説明"
            required
            value={newDesc}
            onChange={setNewDesc}
            maxLength={DESC_MAX}
            rows={5}
          />
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <CustomButton
              label="キャンセル"
              buttonVariant="secondary"
              onClick={closeDrawer}
              disabled={busy}
            />
            <CustomButton
              label="作成"
              buttonVariant="primary"
              onClick={handleCreate}
              disabled={busy}
            />
          </Stack>
        </Stack>
      </ConsoleDetailDrawer>

      {/* 編集ドロワー */}
      <ConsoleDetailDrawer
        open={drawerMode === 'edit' && selected !== null}
        onClose={closeDrawer}
        title={selected?.name ?? ''}
        badges={selected?.isHidden ? <StatusBadge label="非表示" tone="muted" /> : undefined}
        headerActions={
          selected && (
            <Button
              variant="outlined"
              size="small"
              color={selected.isHidden ? 'primary' : 'warning'}
              startIcon={selected.isHidden ? <VisibilityIcon /> : <VisibilityOffIcon />}
              onClick={handleToggleHidden}
              disabled={busy}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                whiteSpace: 'nowrap',
                boxShadow: 'none',
              }}
            >
              {selected.isHidden ? '再表示' : '非表示にする'}
            </Button>
          )
        }
        width={640}
      >
        {selected && (
          <>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                mb: 2,
                p: 1.25,
                border: 1,
                borderColor: 'divider',
                borderRadius: 2,
                bgcolor: 'action.hover',
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" fontWeight={600}>
                  {selected.isHidden ? 'この課題は非表示です' : 'この課題は有効です'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  非表示にすると割り当て候補・一覧の既定から除外されます（割当済みには影響しません）
                </Typography>
              </Box>
              <Button
                variant="outlined"
                size="small"
                color={selected.isHidden ? 'primary' : 'warning'}
                startIcon={selected.isHidden ? <VisibilityIcon /> : <VisibilityOffIcon />}
                onClick={handleToggleHidden}
                disabled={busy}
                sx={{
                  flexShrink: 0,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  boxShadow: 'none',
                }}
              >
                {selected.isHidden ? '再表示' : '非表示にする'}
              </Button>
            </Box>
            <DrawerSection title="基本情報">
              <Stack spacing={2}>
                <TextField
                  label="課題名"
                  required
                  size="small"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  slotProps={{ htmlInput: { maxLength: NAME_MAX } }}
                  helperText={`${editName.length}/${NAME_MAX}`}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
                <TextArea
                  label="課題説明"
                  required
                  value={editDesc}
                  onChange={setEditDesc}
                  maxLength={DESC_MAX}
                  rows={4}
                />
                <Stack direction="row" justifyContent="flex-end">
                  <CustomButton
                    label="更新"
                    buttonVariant="primary"
                    onClick={handleUpdate}
                    disabled={busy}
                  />
                </Stack>
              </Stack>
            </DrawerSection>

            <DrawerSection
              title={`設問（${selected.questions.length}）`}
              action={
                <CustomButton
                  label="設問を追加"
                  buttonVariant="secondary"
                  icon={<AddIcon />}
                  onClick={() => setAddQuestionsOpen(true)}
                />
              }
            >
              {!isUnassigned && (
                <Alert severity="info" sx={{ borderRadius: 2, mb: 1.5 }}>
                  この課題は既にユーザーに割り当てられているため、追加済みの設問は取り消せません。
                </Alert>
              )}
              {selected.questions.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  設問がありません。設問を追加してください。
                </Typography>
              ) : (
                <Stack spacing={1}>
                  {selected.questions.map((q) => (
                    <Box
                      key={q.questionId}
                      sx={{
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 2,
                        px: 1.5,
                        py: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={600} noWrap>
                          {q.title}
                        </Typography>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          <Chip
                            size="small"
                            label={QuestionSourceType[q.sourceType]}
                            variant="outlined"
                            sx={{ borderRadius: 1, height: 18, fontSize: 10 }}
                          />
                          {q.extensions.map((ext) => (
                            <LanguageChip key={ext} extension={ext} />
                          ))}
                        </Stack>
                      </Box>
                      {isUnassigned && (
                        <Tooltip title="追加を取り消す">
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveQuestion(q.questionId)}
                          >
                            <RemoveCircleOutlineIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  ))}
                </Stack>
              )}
            </DrawerSection>

            <DrawerSection
              title={`割り当て状況（${selected.assignedUsers.length}）`}
              action={
                <CustomButton
                  label="ユーザーに割り当て"
                  buttonVariant="secondary"
                  icon={<GroupAddIcon />}
                  onClick={() => setAssignUsersOpen(true)}
                />
              }
            >
              {selected.assignedUsers.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  まだ誰にも割り当てられていません。
                </Typography>
              ) : (
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                  {selected.assignedUsers.map((u) => (
                    <Chip
                      key={u.userId}
                      size="small"
                      icon={<PersonIcon sx={{ fontSize: 16 }} />}
                      label={u.userName}
                      sx={{ borderRadius: 1.5 }}
                    />
                  ))}
                </Stack>
              )}
            </DrawerSection>
          </>
        )}
      </ConsoleDetailDrawer>

      {selected && (
        <>
          <AddQuestionsDialog
            open={addQuestionsOpen}
            onClose={() => setAddQuestionsOpen(false)}
            existingQuestionIds={selected.questions.map((q) => q.questionId)}
            onAdd={handleAddQuestions}
            submitting={busy}
          />
          <AssignUsersDialog
            open={assignUsersOpen}
            onClose={() => setAssignUsersOpen(false)}
            assignedUserIds={selected.assignedUsers.map((u) => u.userId)}
            onAssign={handleAssignUsers}
            submitting={busy}
          />
        </>
      )}
    </Box>
  );
};

export default AssignmentManagement;
